import { StatusIndicator } from '@cloudscape-design/components';
import type { StatusIndicatorProps } from '@cloudscape-design/components';

import type { VoiceSalesCampaign, VoiceSalesTarget } from '../../common/api/voiceSales';
import { formatDateStringWithTime } from '../../common/formatting/dateFormat';

export function statusType(status?: string | null, error?: string | null): StatusIndicatorProps.Type {
  if (error || status === 'failed' || status === 'error') return 'error';
  if (status === 'completed' || status === 'dry_run_sent' || status === 'eligible') return 'success';
  if (status === 'running' || status === 'provider_initiated') return 'in-progress';
  if (status === 'scheduled' || status === 'draft') return 'pending';
  return 'info';
}

const STATUS_LABELS: Record<string, string> = {
  completed: 'Completed',
  draft: 'Draft',
  dry_run: 'Preview mode',
  dry_run_sent: 'Preview complete',
  eligible: 'Eligible',
  error: 'Needs attention',
  failed: 'Needs attention',
  provider_initiated: 'Connecting',
  running: 'In progress',
  scheduled: 'Scheduled',
  scheduler_link_sent: 'Scheduler link sent',
  sent: 'Sent',
};

export function formatStatusLabel(status?: string | null): string {
  if (!status) return 'Not available';
  const knownLabel = STATUS_LABELS[status.toLowerCase()];
  if (knownLabel) return knownLabel;
  const words = status.replace(/[._-]+/g, ' ').trim().toLowerCase();
  return words ? `${words.charAt(0).toUpperCase()}${words.slice(1)}` : 'Not available';
}

export function VoiceSalesStatus({ status, error }: { status?: string | null; error?: string | null }) {
  return <StatusIndicator type={statusType(status, error)}>{formatStatusLabel(status)}</StatusIndicator>;
}

export function formatDuration(seconds?: number | null): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

export function formatSourceLabel(source: string): string {
  const words = source
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? `${words.charAt(0).toUpperCase()}${words.slice(1)}` : 'Unknown source';
}

function metadataString(metadata: VoiceSalesTarget['metadata_json'], key: string): string | null {
  const value = metadata[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function metadataBoolean(metadata: VoiceSalesTarget['metadata_json'], key: string): boolean | null {
  const value = metadata[key];
  return typeof value === 'boolean' ? value : null;
}

export function formatOptOutStatus(target: VoiceSalesTarget): string {
  const { do_not_call: doNotCall, opt_out_at: optOutAt } = target.compliance;
  if (optOutAt) return `Opted out ${formatDateStringWithTime(optOutAt)}`;
  if (doNotCall === true) return 'Do-not-call flag set';
  if (doNotCall === false) return 'No opt-out flag recorded';
  return 'Unknown / not recorded';
}

export function formatComplianceMetadata(target: VoiceSalesTarget): string {
  const lineType = metadataString(target.metadata_json, 'line_type') ?? 'unknown line type';
  const dncScrubbedAt = metadataString(target.metadata_json, 'dnc_scrubbed_at');
  const dncStatus = metadataString(target.metadata_json, 'dnc_status') ?? 'unknown / not recorded';
  const sourceState = metadataString(target.metadata_json, 'source_state');
  return [
    `Phone type: ${formatStatusLabel(lineType)}`,
    `Opt-out: ${formatOptOutStatus(target)}`,
    `DNC status: ${formatStatusLabel(dncStatus)}`,
    `DNC scrubbed: ${dncScrubbedAt ? formatDateStringWithTime(dncScrubbedAt) : 'not recorded'}`,
    `Last checked: ${formatDateStringWithTime(target.last_checked_at)}`,
    sourceState ? `Source state: ${sourceState}` : null,
  ]
    .filter(Boolean)
    .join('\n');
}

export function campaignOption(campaign: VoiceSalesCampaign) {
  return {
    label: campaign.name,
    value: String(campaign.id),
    description: `${campaign.source_region} · ${formatStatusLabel(campaign.status)} · ${campaign.daily_cap} calls/day`,
  };
}
